import { type ComponentProps, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import CloseIcon from '../../../../assets/icons/close-icon.svg?react';
import MenuHamburgerIcon from '../../../../assets/icons/menu-hamburger.svg?react';
import { LinkButton } from '../../button/LinkButton';
import { theme } from '../../theme/theme';

const NavSC = styled.nav`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ButtonGroupSC = styled.div`
  display: none;

  @media (min-width: ${(props) => props.theme.devices.tablet}) {
    display: flex;
    gap: 1rem;
  }
`;

const MobileToggleSC = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${(props) => props.theme.colors.white};

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }

  @media (min-width: ${(props) => props.theme.devices.tablet}) {
    display: none;
  }
`;

const MobileMenuSC = styled.div<{ $isOpen: boolean }>`
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background-color: ${(props) => props.theme.colors.oxfordBlue[100]};
  border: 1px solid ${(props) => props.theme.colors.oxfordBlue[80]};
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.25);
  z-index: 10;
  min-width: 9rem;

  > a,
  > button {
    width: 100%;
  }

  @media (min-width: ${(props) => props.theme.devices.tablet}) {
    display: none;
  }
`;

const StyledHamburgerIcon = styled(MenuHamburgerIcon)`
  path {
    fill: currentColor;
  }
`;

const StyledCloseIcon = styled(CloseIcon)`
  path {
    stroke: currentColor;
  }
`;

type Link = {
  key: string;
  label: string;
  props: Omit<ComponentProps<typeof LinkButton>, 'children'>;
};

type Props = {
  links: Link[];
};

export const NavLinks = ({ links }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const renderLinkButtons = () => {
    return links.map(({ key, label, props }) => (
      <LinkButton key={key} role="link" {...props}>
        {label}
      </LinkButton>
    ));
  };

  return (
    <NavSC ref={containerRef}>
      <ButtonGroupSC>{renderLinkButtons()}</ButtonGroupSC>
      <MobileToggleSC
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        onClick={handleToggle}
        type="button"
      >
        {isOpen ? <StyledCloseIcon color={theme.colors.white} /> : <StyledHamburgerIcon color={theme.colors.white} />}
      </MobileToggleSC>
      <MobileMenuSC $isOpen={isOpen}>{renderLinkButtons()}</MobileMenuSC>
    </NavSC>
  );
};
